import React, { useState, useEffect, useRef } from 'react';
import { CheckCircle2, X, BellRing } from 'lucide-react';

const ETI_LOGO = 'https://customer-assets.emergentagent.com/job_4e0bdddc-c844-4374-a91a-dfbddecb14b1/artifacts/4ane8ulw_eti%20.png';

const GENERAL_REMINDERS = [
  'Switch off the electronics when not in use.',
  'Always take care of the devices and gadgets provided by the company.',
  'Keep your personal phones away or in your drawer during office hours.',
  'Take ownership of the tasks assigned to you.',
];

const ROLE_REMINDERS = {
  'Front Desk Executive': [
    'Make sure there is no clutter at the reception area.',
    'Greet everyone in a professional manner.',
    'Keep a record of every payment in the CRM & Day Book.',
    'Immediately provide the Fee Receipt and Enrollment Profile to candidates.',
  ],
  'Counsellor': [
    'Make sure every lead is handled promptly.',
    'Send proper course details to every student.',
    'Guide every student for the certifications.',
    'Clearly explain the fee plan, course modules, certification & exam parts during counselling.',
  ],
  'Branch Admin': [
    'Record every payment and expense in the CRM.',
    'Daily check the number of leads, conversions and cash flow.',
  ],
  'Trainer': [
    'Make sure the class starts on time.',
    'Every day the student must learn a new topic.',
    'Mark student attendance daily.',
    'Keep yourself updated with the latest tools and technologies.',
  ],
  'Placement Manager': [
    'Follow up with every placement-ready student promptly.',
    'Keep the student database and resumes up to date.',
    'Coordinate interview schedules with companies and update outcomes in the CRM.',
    'Build and nurture strong relationships with hiring partners.',
  ],
  'Certificate Manager': [
    'Process every certificate request promptly and accurately.',
    'Verify student details before issuing any certificate.',
    'Keep proper records of issued, printed and dispatched certificates.',
    'Hand over / dispatch certificates to students on time.',
  ],
  'Academic Controller': [
    'Keep the curriculum and course modules updated with the latest syllabus.',
    'Create and review quiz exams for quality and accuracy.',
    'Ensure exam schedules are shared and followed on time.',
    'Coordinate with trainers to maintain academic quality.',
  ],
};

const ROLE_LABELS = {
  'Front Desk Executive': 'Front Desk',
  'Counsellor': 'Counsellor',
  'Branch Admin': 'Branch Admin',
  'Trainer': 'Trainer',
  'Placement Manager': 'Placement Manager',
  'Certificate Manager': 'Certificate Manager',
  'Academic Controller': 'Academic Controller',
};

const AUTO_CLOSE_SECONDS = 20;

const LoginReminders = () => {
  const [open, setOpen] = useState(false);
  const [role, setRole] = useState('');
  const [seconds, setSeconds] = useState(AUTO_CLOSE_SECONDS);
  const timerRef = useRef(null);

  useEffect(() => {
    if (localStorage.getItem('show_reminders') === '1') {
      localStorage.removeItem('show_reminders');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const today = new Date().toDateString();
      const key = `reminders_last_shown_${user.id || user.email || 'user'}`;
      // Show only once per day per user
      if (localStorage.getItem(key) === today) return;
      localStorage.setItem(key, today);
      setRole(user.role || '');
      setSeconds(AUTO_CLOSE_SECONDS);
      setOpen(true);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    timerRef.current = setInterval(() => {
      setSeconds((s) => {
        if (s <= 1) {
          clearInterval(timerRef.current);
          setOpen(false);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [open]);

  if (!open) return null;

  const roleReminders = ROLE_REMINDERS[role] || [];
  const roleLabel = ROLE_LABELS[role] || role;
  const progress = (seconds / AUTO_CLOSE_SECONDS) * 100;

  return (
    <div
      data-testid="login-reminders-overlay"
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4 animate-in fade-in duration-200"
    >
      <div
        data-testid="login-reminders-modal"
        className="relative w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200 animate-in zoom-in-95 slide-in-from-bottom-2 duration-300"
      >
        {/* Top progress bar */}
        <div className="absolute top-0 left-0 h-1 w-full bg-slate-100">
          <div
            className="h-full bg-blue-600 transition-all duration-1000 ease-linear"
            style={{ width: `${progress}%` }}
            data-testid="login-reminders-progress"
          />
        </div>

        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-6 pt-6 pb-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <img src={ETI_LOGO} alt="ETI Educom" className="h-9 w-auto" />
            <div>
              <div className="flex items-center gap-2">
                <BellRing className="h-5 w-5 text-blue-600" />
                <h2 className="text-lg font-bold text-slate-900">Things to Remember</h2>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">
                A quick note before you start your day{roleLabel ? ` — ${roleLabel}` : ''}.
              </p>
            </div>
          </div>
          <button
            data-testid="login-reminders-close"
            onClick={() => setOpen(false)}
            className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 max-h-[60vh] overflow-y-auto space-y-5">
          <div>
            <h3 className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-3">
              General
            </h3>
            <ul className="space-y-2.5">
              {GENERAL_REMINDERS.map((item, i) => (
                <li key={`g-${i}`} className="flex items-start gap-3 text-sm text-slate-700">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-500" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {roleReminders.length > 0 && (
            <div>
              <h3 className="text-[11px] font-semibold uppercase tracking-wider text-blue-500 mb-3">
                For {roleLabel}
              </h3>
              <ul className="space-y-2.5">
                {roleReminders.map((item, i) => (
                  <li key={`r-${i}`} className="flex items-start gap-3 text-sm text-slate-700">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-500" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 bg-slate-50 border-t border-slate-100">
          <span className="text-xs text-slate-400" data-testid="login-reminders-countdown">
            This will close automatically in {seconds}s
          </span>
          <button
            data-testid="login-reminders-got-it"
            onClick={() => setOpen(false)}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 transition-colors"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};

export default LoginReminders;
